import React, {FC, useEffect, useState} from "react"
import * as PropTypes from "prop-types"
import {isNull} from "lodash"

import {FormattedCurrency, FormattedMessage, FormattedNumber} from "@irati/utils/lang"
import {Row} from "react-bootstrap"
import {PaymentCurrencyType, WalletEntityType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {H2, H6, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import {Checkbox, Input, ValidationButton} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import RecipientList from "@irati-web/reward/src/components/Checkout/Steps/Recipients/RecipientList"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import WalletBallance from "@irati/wallet/apollo/query/Wallet"
import authentication from "@irati/auth"
import Spacing from "@irati/ui/src/Spacing"
import {noDecimal} from "@irati/utils/validation/regex/number"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Loading from "@irati/ui/src/Loading"
import ErrorText from "@irati/ui/src/ErrorText"
import {RouterLink} from "@irati/ui/src/Link"
import {useBudgetsPermissions} from "@irati/wallet/hooks/budget"
import {InjectIntlProps, injectIntl} from "@irati/utils/lang/injectIntl"
import {DistributeRewardImportType, DistributeRewardRecipient} from "../../../enums/DistributeRewardTypes"
import {useRewardsDistribution, useRewardsDistributionActions} from "../../../stores/DistributeReward/hooks"
import TemplatePreview from "../../TemplatePreview"
import BudgetCategory from "./BudgetCategory"

interface Props extends InjectIntlProps {}

const DistributeRewardSummaryStep1: FC<Props> = ({formatMessage}) => {
  const theme = useTheme()
  const payload = useRewardsDistribution()
  const {dispatch, createBulkReward, modifyBulkReward} = useRewardsDistributionActions()
  const [showCSVError, setShowCSVError] = useState(false)
  const [submitting, setSubmitting] = useState<Promise<void>>()
  const {canBudget} = useBudgetsPermissions()

  const selectedCategory = payload.category
  const unassignedAmount = (payload.unassigned?.amount ?? 0) / 100
  const categoryAmount = selectedCategory?.amount! / 100
  const selectedTotalAmount = categoryAmount + unassignedAmount

  useEffect(() => {
    if (!payload.bulkRewardState.csvError) {
      setShowCSVError(false)
    }
  }, [payload.bulkRewardState])

  useEffect(() => {
    if (categoryAmount > payload.pointsPerUser) {
      dispatch({
        type: "SET_CHECK_UNASSIGNED",
        payload: {
          checkUnassigned: false
        }
      })
    }
  }, [payload.pointsPerUser])

  const totUsers =
    payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV ? payload.userEmails.length : payload.destinationUsers.length
  const totalToPay = payload.pointsPerUser * totUsers || 0

  return (
    <>
      <TemplatePreview generic={payload.step === "recipients"} boxed />
      <H6 paddingLeft={3} paddingBottom={3} paddingTop={4}>
        <FormattedMessage id="wallet.reward.admin.summary.title" />
      </H6>

      <BudgetCategory payload={payload} dispatch={dispatch} />
      <Flex direction="column" padding={[0, 3, 3]}>
        <Label large>
          <FormattedMessage id="wallet.reward.admin.distribute.enterPoints.title" />
        </Label>
        <WalletBallance
          variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
          {(apollo) => {
            return (
              <Row>
                <Input
                  name="pointsPerUser"
                  type="number"
                  min="1"
                  value={String(payload.pointsPerUser)}
                  placeholder="wallet.reward.admin.distribute.enterPoints.placeholder"
                  validation={
                    canBudget
                      ? canBudgetDistributeRewardPointsSchema(
                          Math.round(apollo.data.wallet.amount / 100),
                          totUsers,
                          payload.budgetCategory === payload.unassigned?.name ? unassignedAmount : selectedTotalAmount
                        )
                      : distributeRewardPointsSchema(Math.round(apollo.data.wallet.amount / 100), totUsers)
                  }
                  onChange={(pointsPerUser) => dispatch({type: "SET_USER_POINTS", payload: {userPoints: pointsPerUser}})}
                />
              </Row>
            )
          }}
        </WalletBallance>
      </Flex>

      {canBudget && categoryAmount < payload.pointsPerUser * totUsers && (
        <WalletBallance
          variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
          {(apollo) =>
            totalToPay <= apollo.data.wallet.amount / 100 && (
              <Flex paddingLeft={1.5} paddingRight={3}>
                <Checkbox
                  name="check_unassigned"
                  id="checkUnassigned"
                  checked={payload.checkUnassigned}
                  onChange={() =>
                    dispatch({
                      type: "SET_CHECK_UNASSIGNED",
                      payload: {
                        checkUnassigned: !payload.checkUnassigned
                      }
                    })
                  }
                  validation={Validation.bool().oneOf([true], formatMessage({id: "platform.defaultValidation.required"}))}>
                  <Label color={payload.pointsPerUser * totUsers - categoryAmount > unassignedAmount && theme.colors.tertiary.r_400}>
                    <FormattedMessage
                      id="wallet.reward.budget.distribute.checkbox.insufficientFunds"
                      values={{
                        currentBalance: <FormattedNumber value={payload.pointsPerUser * totUsers - categoryAmount} />,
                        pointAllocation: <FormattedNumber value={unassignedAmount} />
                      }}
                    />
                  </Label>
                </Checkbox>
              </Flex>
            )
          }
        </WalletBallance>
      )}

      {!!totUsers && (
        <Spacing paddingLeft={3} paddingRight={3} marginBottom={2}>
          <Label large>
            <FormattedMessage id="wallet.reward.admin.distribute.recipients" />
          </Label>
          {payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV && (
            <Label large marginLeft marginBottom display="block" marginTop={2}>
              <FormattedMessage id="wallet.reward.admin.distribute.recipientsCount" values={{total: totUsers}} />
            </Label>
          )}
          {payload.rewardImportType === DistributeRewardImportType.MANUAL_ENTER && <RecipientList recipients={payload.destinationUsers} />}
        </Spacing>
      )}

      {!canBudget && (
        <Flex style={{background: theme.colors.utility.highlight}} justifyContent="center" paddingTop={2} paddingBottom={2}>
          <Flex direction="column" alignItems="center">
            <Label small marginBottom={0.5}>
              <FormattedMessage id="wallet.reward.intro.available" />
            </Label>
            <WalletBallance
              variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
              {(apollo) => {
                return (
                  <>
                    <FormattedCurrency value={apollo.data.wallet.amount} currency={PaymentCurrencyType.Rp} />
                    {totalToPay > apollo.data.wallet.amount / 100 && (
                      <RouterLink to="/flexi-checkout/top-up" marginTop>
                        <FormattedMessage id="wallet.reward.intro.topup" />
                      </RouterLink>
                    )}
                  </>
                )
              }}
            </WalletBallance>
          </Flex>
        </Flex>
      )}

      <Flex justifyContent="space-between" alignItems="center" padding={3}>
        <Label large>
          <FormattedMessage id="wallet.reward.admin.card.topup.totalToPay" />
        </Label>
        <Flex alignItems="center">
          <Icon icon={IconTypes.rewardPoints} size={32} />
          <WalletBallance
            variables={{userId: authentication.user().uuid, currency: PaymentCurrencyType.Rp, entityType: WalletEntityType.Reward}}>
            {(apollo) =>
              totalToPay > apollo.data.wallet.amount / 100 ? (
                <H2 marginLeft={1} color={theme.colors.tertiary.r_400}>
                  <FormattedNumber value={totalToPay} />
                </H2>
              ) : (
                <H2 marginLeft={1}>
                  <FormattedNumber value={totalToPay} />
                </H2>
              )
            }
          </WalletBallance>
        </Flex>
      </Flex>

      <Flex justifyContent="space-between" paddingLeft={3} paddingRight={3} paddingBottom={3}>
        {payload.step !== "recipients" && (
          <Button
            id="rewardWallet_distributeRewardStep1_back_button"
            onClick={() => {
              switch (payload.step) {
                case "recipients":
                case "message":
                  if (payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV) {
                    dispatch({
                      type: "UPDATE_RECIPIENTS",
                      payload: {
                        recipientsList: []
                      }
                    })
                  }
                  return dispatch({type: "SET_STEP", payload: "recipients"})
                case "confirm":
                  return dispatch({type: "SET_STEP", payload: "message"})
              }
            }}
            marginRight>
            <FormattedMessage id="platform.button.back" />
          </Button>
        )}
        <ValidationButton
          id="rewardWallet_distributeReward_continue_button"
          full
          primary
          onClick={() => {
            const isBulk = payload.rewardImportType === DistributeRewardImportType.IMPORT_CSV

            if (payload.recipientType === DistributeRewardRecipient.NONE) {
              dispatch({type: "SET_RECIPIENT_ERROR", payload: true})
            } else if (isBulk && payload.userEmails.length === 0) {
              setShowCSVError(true)
            } else if (payload.step === "recipients" && isBulk) {
              setSubmitting(createBulkReward(payload))
            } else if (payload.step === "recipients") {
              dispatch({type: "SET_STEP", payload: "message"})
            } else if (payload.step === "message" && isBulk) {
              setSubmitting(modifyBulkReward(payload))
            } else if (payload.step === "message") {
              dispatch({type: "SET_STEP", payload: "confirm"})
            }
          }}
          load={submitting}
          loading={formatMessage({id: "platform.button.validating"})}>
          <FormattedMessage id="platform.button.continue" />
        </ValidationButton>
      </Flex>

      <Flex justifyContent="center" margin={[0, 2]} paddingBottom={2}>
        <Loading useLoader={false} load={submitting} />
        {showCSVError && (
          <ErrorText>
            <FormattedMessage
              id={
                payload.bulkRewardState.csvError
                  ? "wallet.adminReward.bulkRewards.errors.csvHasErrors"
                  : "wallet.adminReward.bulkRewards.errors.incorrectCSV"
              }
            />
          </ErrorText>
        )}
      </Flex>
    </>
  )
}

DistributeRewardSummaryStep1.contextTypes = {
  appPath: PropTypes.string.isRequired
}

const MIN_AMOUNT = 1

function distributeRewardPointsSchema(pointsInWallet: number, numberOfUsers: number) {
  return Validation.string("wallet.reward.admin.distribute.validation.pointsPerUser.required")
    .matches(noDecimal, "wallet.reward.admin.distribute.validation.integersOnly")
    .test(
      "Positive, value above 1 and no decimal",
      "wallet.reward.admin.distribute.validation.pointsPerUser.minOne",
      (val) => val >= MIN_AMOUNT || isNull(val)
    )
    .test(
      "Value cannot exceed wallet total",
      "wallet.reward.admin.distribute.validation.pointsPerUser.max",
      (val) => val * numberOfUsers <= pointsInWallet
    )
}

function canBudgetDistributeRewardPointsSchema(pointsInWallet: number, numberOfUsers: number, selectedTotalAmount: any) {
  return Validation.string("wallet.reward.admin.distribute.validation.pointsPerUser.required")
    .matches(noDecimal, "wallet.reward.admin.distribute.validation.integersOnly")
    .test(
      "Positive, value above 1 and no decimal",
      "wallet.reward.admin.distribute.validation.pointsPerUser.minOne",
      (val) => val >= MIN_AMOUNT || isNull(val)
    )
    .test(
      "Value cannot exceed wallet total",
      "wallet.reward.admin.distribute.validation.pointsPerUser.max",
      (val) => val * numberOfUsers <= pointsInWallet
    )
    .test(
      "Insufficient funds",
      "wallet.reward.budget.distribute.validation.insufficientFunds",
      (val) => val * numberOfUsers <= selectedTotalAmount
    )
}

export default injectIntl(DistributeRewardSummaryStep1)
